import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheckCircle,
  faEnvelope,
  faEye,
  faEyeSlash,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScrollLink } from './ScrollLink';
import authService from '../services/authService';
import '../styles/RecuperacaoSenha.css';

function RecuperacaoSenha() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';
  const isResetMode = Boolean(token);

  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [completed, setCompleted] = useState(false);

  const firstError = (errors, field) => {
    const value = errors?.[field];
    return Array.isArray(value) ? value[0] : value;
  };

  const handleRequestLink = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setFieldErrors({});
    setLoading(true);

    try {
      const response = await authService.requestPasswordReset(email);
      setMessage(response.data.message);
      setCompleted(true);
    } catch (requestError) {
      const response = requestError.response?.data;
      setFieldErrors(response?.errors || {});
      setError(response?.error || 'Não foi possível enviar o link. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    const errors = {};
    if (!email) {
      errors.email = 'Informe seu e-mail.';
    }
    if (password.length < 6 || password.length > 72) {
      errors.password = password.length < 6
        ? 'A senha deve ter pelo menos 6 caracteres.'
        : 'A senha deve ter no máximo 72 caracteres.';
    }
    if (password !== passwordConfirmation) {
      errors.password_confirmation = 'As senhas não coincidem.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setLoading(true);

    try {
      const response = await authService.resetPassword({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      setMessage(response.data.message);
      setCompleted(true);
    } catch (resetError) {
      const response = resetError.response?.data;
      setFieldErrors(response?.errors || {});
      setError(
        response?.error
          || 'Não foi possível redefinir a senha. Solicite um novo link.'
      );
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    navigate(`/login?email=${encodeURIComponent(email.trim())}`, {
      replace: true,
      state: { passwordResetSuccess: true },
    });
  };

  return (
    <main className="password-recovery-page">
      <section className="password-recovery-card" aria-labelledby="password-recovery-title">
        <div className="password-recovery-icon" aria-hidden="true">
          <FontAwesomeIcon icon={completed ? faCheckCircle : (isResetMode ? faLock : faEnvelope)} />
        </div>

        <h1 id="password-recovery-title">
          {completed
            ? (isResetMode ? 'Senha redefinida' : 'Confira seu e-mail')
            : (isResetMode ? 'Crie uma nova senha' : 'Recupere seu acesso')}
        </h1>

        {!completed && (
          <p className="password-recovery-description">
            {isResetMode
              ? 'Informe o e-mail da sua conta e escolha uma nova senha.'
              : 'Informe o e-mail cadastrado para receber um link seguro de recuperação.'}
          </p>
        )}

        <div className="password-recovery-feedback" aria-live="polite">
          {message && <div className="password-recovery-message success">{message}</div>}
          {error && <div className="password-recovery-message error" role="alert">{error}</div>}
        </div>

        {!completed && !isResetMode && (
          <form className="password-recovery-form" onSubmit={handleRequestLink}>
            <div className="password-recovery-group">
              <label htmlFor="recovery-email">E-mail</label>
              <div className="password-recovery-input">
                <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
                <input
                  id="recovery-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Digite seu e-mail"
                  autoComplete="email"
                  maxLength="255"
                  required
                />
              </div>
              {firstError(fieldErrors, 'email') && (
                <span className="password-recovery-field-error">
                  {firstError(fieldErrors, 'email')}
                </span>
              )}
            </div>

            <button className="password-recovery-submit" type="submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar link de recuperação'}
            </button>
          </form>
        )}

        {!completed && isResetMode && (
          <form className="password-recovery-form" onSubmit={handleResetPassword}>
            <div className="password-recovery-group">
              <label htmlFor="reset-email">E-mail</label>
              <div className="password-recovery-input">
                <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Digite seu e-mail"
                  autoComplete="email"
                  maxLength="255"
                  required
                />
              </div>
              {firstError(fieldErrors, 'email') && (
                <span className="password-recovery-field-error">
                  {firstError(fieldErrors, 'email')}
                </span>
              )}
            </div>

            <div className="password-recovery-group">
              <label htmlFor="new-password">Nova senha</label>
              <div className="password-recovery-input password">
                <FontAwesomeIcon icon={faLock} aria-hidden="true" />
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Mínimo de 6 caracteres"
                  autoComplete="new-password"
                  minLength="6"
                  maxLength="72"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? 'Ocultar nova senha' : 'Mostrar nova senha'}
                  aria-pressed={showPassword}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {firstError(fieldErrors, 'password') && (
                <span className="password-recovery-field-error">
                  {firstError(fieldErrors, 'password')}
                </span>
              )}
            </div>

            <div className="password-recovery-group">
              <label htmlFor="new-password-confirmation">Confirmar nova senha</label>
              <div className="password-recovery-input password">
                <FontAwesomeIcon icon={faLock} aria-hidden="true" />
                <input
                  id="new-password-confirmation"
                  type={showConfirmation ? 'text' : 'password'}
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  placeholder="Digite a senha novamente"
                  autoComplete="new-password"
                  minLength="6"
                  maxLength="72"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmation((visible) => !visible)}
                  aria-label={showConfirmation ? 'Ocultar confirmação' : 'Mostrar confirmação'}
                  aria-pressed={showConfirmation}
                >
                  <FontAwesomeIcon icon={showConfirmation ? faEyeSlash : faEye} />
                </button>
              </div>
              {firstError(fieldErrors, 'password_confirmation') && (
                <span className="password-recovery-field-error">
                  {firstError(fieldErrors, 'password_confirmation')}
                </span>
              )}
            </div>

            <button className="password-recovery-submit" type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Redefinir senha'}
            </button>
          </form>
        )}

        {completed && isResetMode && (
          <button type="button" className="password-recovery-submit" onClick={goToLogin}>
            Entrar com a nova senha
          </button>
        )}

        <div className="password-recovery-back">
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          <ScrollLink to="/login">Voltar para o login</ScrollLink>
        </div>
      </section>
    </main>
  );
}

export default RecuperacaoSenha;
